import { useState } from "react";
import { Alert, Button, Stack, TextField } from "@mui/material";
import { CopyButton } from "../../../components/developerTools/CopyButton";
import { ToolLayout } from "../../../components/developerTools/ToolLayout";
import { encodeRandomBytes, generateRandomBytes, MAXIMUM_RANDOM_BYTES, RandomByteEncoding } from "../../../utils/randomBytes";



const BYTE_PRESETS = [ 16, 24, 32, 64, 128 ];

/** Displays a secure random byte generator that keeps values in browser memory only. */
export const RandomBytesTool = () => {
	const [ byteLength, setByteLength ] = useState("32");
	const [ encoding, setEncoding ] = useState<RandomByteEncoding>("base64");
	const [ bytes, setBytes ] = useState<Uint8Array | null>(null);
	const [ error, setError ] = useState("");
	const output = bytes ? encodeRandomBytes(bytes, encoding) : "";
	const changeLength = (value: string) => {
		setByteLength(value);
		setBytes(null);
		setError("");
	};
	const generate = () => {
		const result = generateRandomBytes(Number(byteLength));
		setBytes(result.success ? result.value : null);
		setError(result.success ? "" : result.error);
	};
	return (
		<ToolLayout title="Secure Random Byte Generator" description="Generate cryptographically secure random bytes in Base64, Hex or Base64URL. Values are generated locally in your browser and are never sent to NumaSuite." local>
			<Stack spacing={ 3 }>
				<Alert severity="info">Generated locally — random values are created using your browser's cryptographically secure random number generator and never leave your browser.</Alert>
				<TextField label="Number of bytes" type="number" value={ byteLength } onChange={ (event) => changeLength(event.target.value) } inputProps={{ min: 1, max: MAXIMUM_RANDOM_BYTES, step: 1 }} helperText={ `Choose a preset or enter a whole number from 1 to ${ MAXIMUM_RANDOM_BYTES }.` } />
				<Stack direction="row" gap={ 1 } flexWrap="wrap" aria-label="Byte length presets">
					{ BYTE_PRESETS.map((length) => <Button key={ length } variant="outlined" aria-pressed={ byteLength === String(length) } onClick={ () => changeLength(String(length)) }>{ length } bytes</Button>) }
				</Stack>
				<TextField label="Output encoding" select SelectProps={{ native: true }} value={ encoding } onChange={ (event) => {
					const value = event.target.value;
					if(value === "base64" || value === "hex" || value === "base64url") setEncoding(value);
				}}>
					<option value="base64">Base64</option>
					<option value="hex">Hex</option>
					<option value="base64url">Base64URL</option>
				</TextField>
				<Stack direction="row" gap={ 2 } flexWrap="wrap">
					<Button variant="contained" onClick={ generate }>Generate</Button>
					<Button variant="outlined" onClick={ () => { setBytes(null); setError(""); } }>Clear</Button>
				</Stack>
				{ error && <Alert severity="error">{ error }</Alert> }
				<TextField label="Output" multiline minRows={ 4 } fullWidth value={ output } inputProps={{ readOnly: true, spellCheck: false }} />
				<CopyButton value={ output } />
			</Stack>
		</ToolLayout>
	);
};
